import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ExternalLink, RefreshCw, Star, AlertCircle, TrendingUp, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';
import PropsSection from './PropsSection';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Sports that support player props (must match backend Config.PROPS_ENABLED_SPORTS)
const PROPS_ENABLED_SPORTS = [
  'basketball_nba',
  'basketball_ncaab',
  'basketball_ncaaw',
  'basketball_wnba',
  'baseball_mlb',
  'americanfootball_nfl',
  'americanfootball_ncaaf',
];

const SPORTSBOOK_URLS = {
  draftkings: 'https://sportsbook.draftkings.com',
  fanduel: 'https://sportsbook.fanduel.com',
  betmgm: 'https://sports.betmgm.com',
  williamhill_us: 'https://www.caesars.com/sportsbook-and-casino',
  fanatics: 'https://sportsbook.fanatics.com',
  espnbet: 'https://sportsbook.thescore.bet',
  ballybet: 'https://www.ballybet.com',
  betrivers: 'https://www.betrivers.com',
};

function formatOdds(odds) {
  if (odds === null || odds === undefined) return '-';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatDateTime(isoString) {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatPoint(p) {
  if (p === null || p === undefined) return '';
  return p > 0 ? `+${p}` : `${p}`;
}


function formatPeriod(period, sportKey) {
  if (!period) return '';
  if (sportKey?.includes('basketball')) return `Q${period}`;
  if (sportKey?.includes('hockey')) return `P${period}`;
  if (sportKey?.includes('baseball')) {
    const sfx = ['st', 'nd', 'rd'][period - 1] || 'th';
    return `${period}${sfx} Inn`;
  }
  if (sportKey?.includes('football')) return `Q${period}`;
  if (sportKey?.includes('tennis')) return `Set ${period}`;
  return `P${period}`;
}

// ─── Collapsible Section ────────────────────────────────────────────────────
function CollapsibleSection({ title, children, defaultOpen = true, badge = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="gdp-section">
      <button className="gdp-section-toggle" onClick={() => setOpen(o => !o)}>
        <span className="gdp-section-title">
          {title}
          {badge != null && <span className="gdp-section-badge">{badge}</span>}
        </span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div className="gdp-section-body">{children}</div>}
    </div>
  );
}

// ─── Line Slider ────────────────────────────────────────────────────────────
function LineSlider({ points, selected, onSelect, isMain }) {
  const trackRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (selectedRef.current && trackRef.current) {
      const track = trackRef.current;
      const el = selectedRef.current;
      const scrollLeft = el.offsetLeft - track.offsetLeft - track.clientWidth / 2 + el.offsetWidth / 2;
      track.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
    }
  }, [selected]);

  const idx = points.indexOf(selected);
  const canPrev = idx > 0;
  const canNext = idx < points.length - 1;
  const fmtPt = pt => pt > 0 ? `+${pt}` : `${pt}`;

  return (
    <div className="line-slider">
      <button className="line-slider-arrow" onClick={() => canPrev && onSelect(points[idx - 1])} disabled={!canPrev}>
        <ChevronLeft size={14} />
      </button>
      <div className="line-slider-track" ref={trackRef}>
        {points.map(pt => (
          <button
            key={pt}
            ref={pt === selected ? selectedRef : null}
            className={`line-slider-point${pt === selected ? ' selected' : ''}${isMain && isMain(pt) && pt !== selected ? ' main-line' : ''}`}
            onClick={() => onSelect(pt)}
          >
            {fmtPt(pt)}
          </button>
        ))}
      </div>
      <button className="line-slider-arrow" onClick={() => canNext && onSelect(points[idx + 1])} disabled={!canNext}>
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─── Odds Table ──────────────────────────────────────────────────────────────
function OddsTable({ game, market, altBooks = null, altPoint = null, addToCart, isInCart,
    session, onTrackBet, trackingCell, setTrackingCell, trackAmount, setTrackAmount, trackedKeys }) {
  const books = altBooks || game.bookmakers;

  // Renders the track button or inline form for a specific odds cell
  const renderTrackBtn = (cellKey, trackInfo) => {
    if (!session) return null;
    if (trackingCell === cellKey) {
      const amt = parseFloat(trackAmount);
      const o = trackInfo.odds;
      const toWin = amt > 0 && o ? (amt * (o > 0 ? o / 100 : 100 / Math.abs(o))).toFixed(2) : null;
      return (
        <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
          <input
            type="number"
            className="gdp-track-input"
            placeholder="$"
            value={trackAmount}
            onChange={e => setTrackAmount(e.target.value)}
            autoFocus
          />
          <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey, ...trackInfo })}>✓</button>
          <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
          {toWin && <span className="ctf-to-win">To win: +${toWin}</span>}
        </div>
      );
    }
    return (
      <button
        className={`gdp-track-btn${trackedKeys.has(cellKey) ? ' tracked' : ''}`}
        onClick={e => { e.stopPropagation(); setTrackAmount(''); setTrackingCell(cellKey); }}
      >
        {trackedKeys.has(cellKey) ? '✓' : 'Track'}
      </button>
    );
  };

  if (market === 'h2h') {
    return (
      <div className="odds-table">
        <div className={`odds-table-header${game.has_draw ? ' three-way' : ''}`}>
          <div className="odds-col-book">Sportsbook</div>
          <div className="odds-col-odds">{game.home_team}</div>
          {game.has_draw && <div className="odds-col-odds">Draw</div>}
          <div className="odds-col-odds">{game.away_team}</div>
        </div>
        {books.map((book) => (
          <div key={book.name} className={`odds-table-row${game.has_draw ? ' three-way' : ''}`}>
            <div className="odds-col-book">
              <SportsbookLogo bookKey={book.name} size={20} />
              <span className="book-name">{book.title || SPORTSBOOK_NAMES[book.name] || book.name}</span>
              {SPORTSBOOK_URLS[book.name] && (
                <a href={SPORTSBOOK_URLS[book.name]} target="_blank" rel="noopener noreferrer" className="book-link">
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
            {/* Home odds */}
            <div className={`odds-col-odds ${game.best_odds?.home?.book === book.name ? 'best-odds' : ''}`}>
              {trackingCell === `h2h:${game.home_team}:${book.name}` ? (
                <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                  <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                  <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: `h2h:${game.home_team}:${book.name}`, teamRaw: game.home_team, sideDisplay: game.home_team, odds: book.home_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                  <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  {parseFloat(trackAmount) > 0 && book.home_odds && <span className="ctf-to-win">To win: +${(parseFloat(trackAmount) * (book.home_odds > 0 ? book.home_odds / 100 : 100 / Math.abs(book.home_odds))).toFixed(2)}</span>}
                </div>
              ) : (
                <>
                  <span className="odds-value-wrap">
                    {formatOdds(book.home_odds)}
                    {game.best_odds?.home?.book === book.name && <Star size={12} className="best-star" />}
                  </span>
                  {book.home_odds && (
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(game.home_team, 'h2h', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, game.home_team, book.home_odds, 'h2h')}
                      >
                        {isInCart(game.home_team, 'h2h', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(`h2h:${game.home_team}:${book.name}`, { teamRaw: game.home_team, sideDisplay: game.home_team, odds: book.home_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Draw odds */}
            {game.has_draw && (
              <div className={`odds-col-odds ${game.best_odds?.draw?.book === book.name ? 'best-odds' : ''}`}>
                {trackingCell === `h2h:Draw:${book.name}` ? (
                  <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                    <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                    <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: `h2h:Draw:${book.name}`, teamRaw: 'Draw', sideDisplay: 'Draw', odds: book.draw_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                    <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                    {parseFloat(trackAmount) > 0 && book.draw_odds && <span className="ctf-to-win">To win: +${(parseFloat(trackAmount) * (book.draw_odds > 0 ? book.draw_odds / 100 : 100 / Math.abs(book.draw_odds))).toFixed(2)}</span>}
                  </div>
                ) : (
                  <>
                    <span className="odds-value-wrap">
                      {formatOdds(book.draw_odds)}
                      {game.best_odds?.draw?.book === book.name && <Star size={12} className="best-star" />}
                    </span>
                    {book.draw_odds && (
                      <div className="odds-cell-actions">
                        <button
                          className={`add-to-cart-btn ${isInCart('Draw', 'h2h', book.name) ? 'in-cart' : ''}`}
                          onClick={() => addToCart(book, 'Draw', book.draw_odds, 'h2h')}
                        >
                          {isInCart('Draw', 'h2h', book.name) ? '✓ Parlay' : '+ Parlay'}
                        </button>
                        {renderTrackBtn(`h2h:Draw:${book.name}`, { teamRaw: 'Draw', sideDisplay: 'Draw', odds: book.draw_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {/* Away odds */}
            <div className={`odds-col-odds ${game.best_odds?.away?.book === book.name ? 'best-odds' : ''}`}>
              {trackingCell === `h2h:${game.away_team}:${book.name}` ? (
                <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                  <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                  <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: `h2h:${game.away_team}:${book.name}`, teamRaw: game.away_team, sideDisplay: game.away_team, odds: book.away_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                  <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  {parseFloat(trackAmount) > 0 && book.away_odds && <span className="ctf-to-win">To win: +${(parseFloat(trackAmount) * (book.away_odds > 0 ? book.away_odds / 100 : 100 / Math.abs(book.away_odds))).toFixed(2)}</span>}
                </div>
              ) : (
                <>
                  <span className="odds-value-wrap">
                    {formatOdds(book.away_odds)}
                    {game.best_odds?.away?.book === book.name && <Star size={12} className="best-star" />}
                  </span>
                  {book.away_odds && (
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(game.away_team, 'h2h', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, game.away_team, book.away_odds, 'h2h')}
                      >
                        {isInCart(game.away_team, 'h2h', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(`h2h:${game.away_team}:${book.name}`, { teamRaw: game.away_team, sideDisplay: game.away_team, odds: book.away_odds, bookKey: book.name, market: 'h2h', point: null, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (market === 'spreads') {
    const filtered = books.filter(b => b.home_spread_odds);
    if (filtered.length === 0) return null;
    // For alt lines compute best odds from the alt set; for main use backend best_odds
    const bestHomeSpreadBook = altBooks
      ? filtered.reduce((best, b) => !best || b.home_spread_odds > best.home_spread_odds ? b : best, null)?.name
      : game.best_odds?.home_spread?.book;
    const bestAwaySpreadBook = altBooks
      ? filtered.reduce((best, b) => !best || b.away_spread_odds > best.away_spread_odds ? b : best, null)?.name
      : game.best_odds?.away_spread?.book;
    return (
      <div className="odds-table">
        <div className="odds-table-header">
          <div className="odds-col-book">Sportsbook</div>
          <div className="odds-col-odds">{game.home_team}</div>
          <div className="odds-col-odds">{game.away_team}</div>
        </div>
        {filtered.map((book) => {
          const homePoint = book.home_spread_point ?? altPoint;
          const awayPoint = book.away_spread_point ?? (altPoint != null ? -altPoint : null);
          const homeTeamLabel = `${game.home_team} ${formatPoint(homePoint)}`;
          const awayTeamLabel = `${game.away_team} ${formatPoint(awayPoint)}`;
          const homeCellKey = `spreads:${game.home_team}:${homePoint}:${book.name}`;
          const awayCellKey = `spreads:${game.away_team}:${awayPoint}:${book.name}`;
          return (
            <div key={book.name} className="odds-table-row">
              <div className="odds-col-book">
                <SportsbookLogo bookKey={book.name} size={20} />
                <span className="book-name">{book.title || SPORTSBOOK_NAMES[book.name] || book.name}</span>
                {SPORTSBOOK_URLS[book.name] && (
                  <a href={SPORTSBOOK_URLS[book.name]} target="_blank" rel="noopener noreferrer" className="book-link">
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
              {/* Home spread */}
              <div className={`odds-col-odds ${bestHomeSpreadBook === book.name ? 'best-odds' : ''}`}>
                {trackingCell === homeCellKey ? (
                  <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                    <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                    <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: homeCellKey, teamRaw: game.home_team, sideDisplay: homeTeamLabel, odds: book.home_spread_odds, bookKey: book.name, market: 'spreads', point: homePoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                    <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  </div>
                ) : (
                  <>
                    <span className="odds-value-wrap">
                      <span className="spread-point">{formatPoint(homePoint)}</span>
                      <span className="spread-odds">{formatOdds(book.home_spread_odds)}</span>
                      {bestHomeSpreadBook === book.name && <Star size={12} className="best-star" />}
                    </span>
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(homeTeamLabel, 'spreads', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, homeTeamLabel, book.home_spread_odds, 'spreads', homePoint)}
                      >
                        {isInCart(homeTeamLabel, 'spreads', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(homeCellKey, { teamRaw: game.home_team, sideDisplay: homeTeamLabel, odds: book.home_spread_odds, bookKey: book.name, market: 'spreads', point: homePoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  </>
                )}
              </div>
              {/* Away spread */}
              <div className={`odds-col-odds ${bestAwaySpreadBook === book.name ? 'best-odds' : ''}`}>
                {trackingCell === awayCellKey ? (
                  <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                    <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                    <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: awayCellKey, teamRaw: game.away_team, sideDisplay: awayTeamLabel, odds: book.away_spread_odds, bookKey: book.name, market: 'spreads', point: awayPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                    <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  </div>
                ) : (
                  <>
                    <span className="odds-value-wrap">
                      <span className="spread-point">{formatPoint(awayPoint)}</span>
                      <span className="spread-odds">{formatOdds(book.away_spread_odds)}</span>
                      {bestAwaySpreadBook === book.name && <Star size={12} className="best-star" />}
                    </span>
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(awayTeamLabel, 'spreads', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, awayTeamLabel, book.away_spread_odds, 'spreads', awayPoint)}
                      >
                        {isInCart(awayTeamLabel, 'spreads', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(awayCellKey, { teamRaw: game.away_team, sideDisplay: awayTeamLabel, odds: book.away_spread_odds, bookKey: book.name, market: 'spreads', point: awayPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (market === 'totals') {
    const filtered = books.filter(b => b.over_odds);
    if (filtered.length === 0) return null;
    const bestOverBook = altBooks
      ? filtered.reduce((best, b) => !best || b.over_odds > best.over_odds ? b : best, null)?.name
      : game.best_odds?.over?.book;
    const bestUnderBook = altBooks
      ? filtered.reduce((best, b) => !best || b.under_odds > best.under_odds ? b : best, null)?.name
      : game.best_odds?.under?.book;
    return (
      <div className="odds-table">
        <div className="odds-table-header">
          <div className="odds-col-book">Sportsbook</div>
          <div className="odds-col-odds">Over</div>
          <div className="odds-col-odds">Under</div>
        </div>
        {filtered.map((book) => {
          const overPoint = book.over_point ?? altPoint;
          const underPoint = book.under_point ?? altPoint;
          const overLabel = `Over ${overPoint}`;
          const underLabel = `Under ${underPoint}`;
          const overCellKey = `totals:Over:${overPoint}:${book.name}`;
          const underCellKey = `totals:Under:${underPoint}:${book.name}`;
          return (
            <div key={book.name} className="odds-table-row">
              <div className="odds-col-book">
                <SportsbookLogo bookKey={book.name} size={20} />
                <span className="book-name">{book.title || SPORTSBOOK_NAMES[book.name] || book.name}</span>
                {SPORTSBOOK_URLS[book.name] && (
                  <a href={SPORTSBOOK_URLS[book.name]} target="_blank" rel="noopener noreferrer" className="book-link">
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
              {/* Over */}
              <div className={`odds-col-odds ${bestOverBook === book.name ? 'best-odds' : ''}`}>
                {trackingCell === overCellKey ? (
                  <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                    <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                    <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: overCellKey, teamRaw: 'Over', sideDisplay: overLabel, odds: book.over_odds, bookKey: book.name, market: 'totals', point: overPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                    <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  </div>
                ) : (
                  <>
                    <span className="odds-value-wrap">
                      <span className="total-point">O {overPoint}</span>
                      <span className="total-odds">{formatOdds(book.over_odds)}</span>
                      {bestOverBook === book.name && <Star size={12} className="best-star" />}
                    </span>
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(overLabel, 'totals', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, overLabel, book.over_odds, 'totals', overPoint)}
                      >
                        {isInCart(overLabel, 'totals', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(overCellKey, { teamRaw: 'Over', sideDisplay: overLabel, odds: book.over_odds, bookKey: book.name, market: 'totals', point: overPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  </>
                )}
              </div>
              {/* Under */}
              <div className={`odds-col-odds ${bestUnderBook === book.name ? 'best-odds' : ''}`}>
                {trackingCell === underCellKey ? (
                  <div className="gdp-track-form" onClick={e => e.stopPropagation()}>
                    <input type="number" className="gdp-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus />
                    <button className="gdp-track-save" onClick={() => onTrackBet({ cellKey: underCellKey, teamRaw: 'Under', sideDisplay: underLabel, odds: book.under_odds, bookKey: book.name, market: 'totals', point: underPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}>✓</button>
                    <button className="gdp-track-cancel" onClick={() => setTrackingCell(null)}>✕</button>
                  </div>
                ) : (
                  <>
                    <span className="odds-value-wrap">
                      <span className="total-point">U {underPoint}</span>
                      <span className="total-odds">{formatOdds(book.under_odds)}</span>
                      {bestUnderBook === book.name && <Star size={12} className="best-star" />}
                    </span>
                    <div className="odds-cell-actions">
                      <button
                        className={`add-to-cart-btn ${isInCart(underLabel, 'totals', book.name) ? 'in-cart' : ''}`}
                        onClick={() => addToCart(book, underLabel, book.under_odds, 'totals', underPoint)}
                      >
                        {isInCart(underLabel, 'totals', book.name) ? '✓ Parlay' : '+ Parlay'}
                      </button>
                      {renderTrackBtn(underCellKey, { teamRaw: 'Under', sideDisplay: underLabel, odds: book.under_odds, bookKey: book.name, market: 'totals', point: underPoint, bookTitle: book.title || SPORTSBOOK_NAMES[book.name] || book.name })}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
}

// ─── Main Component ──────────────────────────────────────────────────────────
function GameDetailPage({ eventId, sport, eventData, selectedBooks, bankroll = 1000, kellyFraction = 0.25, onBack }) {
  const [game, setGame] = useState(null);
  const [altLines, setAltLines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addSelection, removeSelection, selections } = useCart();
  const { session } = useAuth();

  // Bet tracking state
  const [trackingCell, setTrackingCell] = useState(null);
  const [trackAmount, setTrackAmount] = useState('');
  const [trackedKeys, setTrackedKeys] = useState(new Set());

  // Line slider selection (null = use main line)
  const [selectedSpreadPoint, setSelectedSpreadPoint] = useState(null);
  const [selectedTotalPoint, setSelectedTotalPoint] = useState(null);

  // Props state (NBA, NHL, MLB)
  const [props, setProps] = useState(null);
  const [evProps, setEvProps] = useState([]);
  const [arbProps, setArbProps] = useState([]);
  const [propsBestOdds, setPropsBestOdds] = useState(null);
  const [playerInfo, setPlayerInfo] = useState({});
  const [propsLoading, setPropsLoading] = useState(false);
  const [propsError, setPropsError] = useState(null);

  const isInCart = (team, market, sportsbookKey) =>
    selections.some(s => s.eventId === eventId && s.team === team && s.market === market && s.sportsbookKey === sportsbookKey);

  const getCartSelection = (team, market, sportsbookKey) =>
    selections.find(s => s.eventId === eventId && s.team === team && s.market === market && s.sportsbookKey === sportsbookKey);

  const addToCart = (book, team, odds, market, point = null) => {
    if (!game) return;
    const existing = getCartSelection(team, market, book.name);
    if (existing) {
      removeSelection(existing.id);
      return;
    }
    addSelection({
      eventId: game.id,
      eventName: `${game.home_team} vs ${game.away_team}`,
      team, odds,
      sportsbook: book.title || SPORTSBOOK_NAMES[book.name] || book.name,
      sportsbookKey: book.name,
      market, point,
      oddsAllBooks: {},
      sportKey: game.sport_key,
      homeTeam: game.home_team,
      awayTeam: game.away_team,
    });
  };

  const handleTrackBet = async ({ cellKey, teamRaw, sideDisplay, odds, bookKey, market, point, bookTitle }) => {
    if (!session || !game) return;
    const notes = JSON.stringify({
      event_id: game.id,
      sport_key: game.sport_key,
      home_team: game.home_team,
      away_team: game.away_team,
      side: teamRaw,
      point: point ?? null,
      commence_time: game.commence_time,
    });
    try {
      const resp = await fetch(`${API_BASE_URL}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'manual',
          sportsbook: bookTitle,
          eventName: `${game.home_team} vs ${game.away_team}`,
          sportKey: game.sport_key,
          market,
          side: sideDisplay,
          odds,
          amount: parseFloat(trackAmount) || 0,
          notes,
        }),
      });
      if (resp.ok) {
        setTrackedKeys(prev => { const next = new Set(prev); next.add(cellKey); return next; });
        setTrackingCell(null);
        setTrackAmount('');
      }
    } catch (err) {
      console.error('Track failed:', err);
    }
  };

  const handleTrackProp = async ({ playerName, propType, point, side, odds, bookKey, amount }) => {
    if (!session || !game) return;
    try {
      await fetch(`${API_BASE_URL}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'manual',
          sportsbook: bookKey,
          eventName: `${game.home_team} vs ${game.away_team}`,
          sportKey: game.sport_key,
          market: 'player_props',
          side: `${playerName} ${side} ${point}`,
          odds,
          amount: amount || 0,
          notes: JSON.stringify({
            event_id: game.id,
            sport_key: game.sport_key,
            home_team: game.home_team,
            away_team: game.away_team,
            player_name: playerName,
            prop_type: propType,
            side,
            point,
            commence_time: game.commence_time,
          }),
        }),
      });
    } catch (err) {
      console.error('Track prop failed:', err);
    }
  };

  // Shared props for OddsTable
  const trackingProps = { session, onTrackBet: handleTrackBet, trackingCell, setTrackingCell, trackAmount, setTrackAmount, trackedKeys };

  useEffect(() => {
    const fetchGame = async (isPoll = false) => {
      if (!isPoll) setLoading(true);
      setError(null);
      try {
        let url = `${API_BASE_URL}/game/${eventId}`;
        const params = [];
        if (sport) params.push(`sport=${sport}`);
        if (selectedBooks && selectedBooks.length > 0) params.push(`books=${selectedBooks.join(',')}`);
        if (params.length) url += `?${params.join('&')}`;
        const resp = await fetch(url);
        const result = await resp.json();
        if (result.success) {
          setGame(result.game);
        } else {
          setError(result.error || 'Failed to load game');
        }
      } catch {
        setError('Error connecting to server');
      } finally {
        if (!isPoll) setLoading(false);
      }
    };

    const fetchAltLines = async () => {
      try {
        let url = `${API_BASE_URL}/events/${eventId}/alternate-odds`;
        if (sport) url += `?sport=${sport}`;
        const resp = await fetch(url);
        const result = await resp.json();
        setAltLines(result);
      } catch {
        // Alt lines are optional — silently ignore
      }
    };

    if (eventId) {
      fetchGame();
      fetchAltLines();
    }
    // Auto-refresh: main lines every 60s, alternate lines every 120s
    const gameInterval = setInterval(() => { if (eventId) fetchGame(true); }, 60 * 1000);
    const altInterval = setInterval(() => { if (eventId) fetchAltLines(); }, 120 * 1000);
    return () => { clearInterval(gameInterval); clearInterval(altInterval); };
  }, [eventId, sport, selectedBooks]);

  // Fetch props when game loads (NBA, NHL, MLB)
  useEffect(() => {
    const fetchProps = async (isPoll = false) => {
      if (!game || !PROPS_ENABLED_SPORTS.includes(game.sport_key)) {
        return;
      }

      if (!isPoll) setPropsLoading(true);
      setPropsError(null);

      try {
        const params = new URLSearchParams({
          home_team: game.home_team,
          away_team: game.away_team,
          commence_time: game.commence_time,
          sport: game.sport_key,
        });
        const response = await fetch(`${API_BASE_URL}/game/${game.id}/props?${params}`);
        const result = await response.json();

        if (result.success) {
          setProps(result.props);
          setEvProps(result.ev_props || []);
          setArbProps(result.arb_props || []);
          setPropsBestOdds(result.best_odds);
          setPlayerInfo(result.player_info || {});
        } else {
          setPropsError(result.error || 'Failed to load props');
        }
      } catch {
        setPropsError('Error loading player props');
      } finally {
        if (!isPoll) setPropsLoading(false);
      }
    };

    fetchProps();
    // Auto-refresh player props every 120s
    const propsInterval = setInterval(() => fetchProps(true), 120 * 1000);
    return () => clearInterval(propsInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  // Consensus main-line points (used to merge matching alt books into the main table)
  const mainSpreadPoint = game ? game.bookmakers?.find(b => b.home_spread_odds)?.home_spread_point : null;
  const mainTotalPoint  = game ? game.bookmakers?.find(b => b.over_odds)?.over_point : null;

  // Merged main-line book lists: main bookmakers + any alt books at the same point
  const bookFilterSet = selectedBooks && selectedBooks.length > 0 ? new Set(selectedBooks) : null;

  const mergedSpreadBooks = (() => {
    if (!game || mainSpreadPoint == null || !altLines?.spread_lines) return null;
    const altAtPoint = altLines.spread_lines[String(mainSpreadPoint)] || [];
    if (altAtPoint.length === 0) return null;
    const mainNames = new Set(game.bookmakers.filter(b => b.home_spread_odds).map(b => b.name));
    const extra = altAtPoint.filter(b => !mainNames.has(b.name) && (!bookFilterSet || bookFilterSet.has(b.name)));
    if (extra.length === 0) return null;
    return [...game.bookmakers, ...extra];
  })();

  const mergedTotalBooks = (() => {
    if (!game || mainTotalPoint == null || !altLines?.total_lines) return null;
    const altAtPoint = altLines.total_lines[String(mainTotalPoint)] || [];
    if (altAtPoint.length === 0) return null;
    const mainNames = new Set(game.bookmakers.filter(b => b.over_odds).map(b => b.name));
    const extra = altAtPoint.filter(b => !mainNames.has(b.name) && (!bookFilterSet || bookFilterSet.has(b.name)));
    if (extra.length === 0) return null;
    return [...game.bookmakers, ...extra];
  })();

  // Helper: find alt line books for a numeric point (keys may be "-6.0" not "-6")
  const getAltSpreadBooks = (pt) => {
    if (!altLines?.spread_lines) return [];
    const key = Object.keys(altLines.spread_lines).find(k => Number(k) === pt);
    const books = key ? (altLines.spread_lines[key] || []) : [];
    const filtered = books.filter(b => b.home_spread_odds);
    return bookFilterSet ? filtered.filter(b => bookFilterSet.has(b.name)) : filtered;
  };

  const getAltTotalBooks = (pt) => {
    if (!altLines?.total_lines) return [];
    const key = Object.keys(altLines.total_lines).find(k => Number(k) === pt);
    const books = key ? (altLines.total_lines[key] || []) : [];
    const filtered = books.filter(b => b.over_odds);
    return bookFilterSet ? filtered.filter(b => bookFilterSet.has(b.name)) : filtered;
  };

  // All available points for sliders — only include points that have actual odds
  const allSpreadPoints = (() => {
    const pts = new Set();
    if (mainSpreadPoint != null && game?.bookmakers?.some(b => b.home_spread_odds)) pts.add(mainSpreadPoint);
    Object.keys(altLines?.spread_lines || {}).forEach(p => {
      const numPt = Number(p);
      if (numPt !== mainSpreadPoint && getAltSpreadBooks(numPt).length > 0) pts.add(numPt);
    });
    return [...pts].sort((a, b) => a - b);
  })();

  const allTotalPoints = (() => {
    const pts = new Set();
    if (mainTotalPoint != null && game?.bookmakers?.some(b => b.over_odds)) pts.add(mainTotalPoint);
    Object.keys(altLines?.total_lines || {}).forEach(p => {
      const numPt = Number(p);
      if (numPt !== mainTotalPoint && getAltTotalBooks(numPt).length > 0) pts.add(numPt);
    });
    return [...pts].sort((a, b) => a - b);
  })();

  // Resolved points: selected or fall back to main
  const resolvedSpreadPoint = (selectedSpreadPoint != null && allSpreadPoints.includes(selectedSpreadPoint))
    ? selectedSpreadPoint : mainSpreadPoint;
  const resolvedTotalPoint = (selectedTotalPoint != null && allTotalPoints.includes(selectedTotalPoint))
    ? selectedTotalPoint : mainTotalPoint;

  // Books for the currently selected spread point
  const spreadBooksAtPoint = (() => {
    if (!game) return null;
    if (resolvedSpreadPoint === mainSpreadPoint || resolvedSpreadPoint == null) return mergedSpreadBooks;
    return getAltSpreadBooks(resolvedSpreadPoint);
  })();

  // Books for the currently selected total point
  const totalBooksAtPoint = (() => {
    if (!game) return null;
    if (resolvedTotalPoint === mainTotalPoint || resolvedTotalPoint == null) return mergedTotalBooks;
    return getAltTotalBooks(resolvedTotalPoint);
  })();

  return (
    <div className="game-detail-page">
      <button className="gdp-back-btn" onClick={onBack}>
        <ArrowLeft size={16} />
        Back
      </button>

      {loading && (
        <div className="game-modal-loading">
          <RefreshCw className="spinning" size={24} />
          <p>Loading game details...</p>
        </div>
      )}

      {error && (
        <div className="game-modal-error"><p>{error}</p></div>
      )}

      {game && !loading && (
        <>
          {/* Header */}
          <div className="gdp-header">
            <div className="gdp-sport-badge">{game.sport_title}</div>
            <div className="gdp-matchup">
              <span className="gdp-matchup-team"><TeamLogo teamName={game.away_team} sportKey={game.sport_key} size={28} />{game.away_team}</span>
              <span className="gdp-matchup-sep">vs</span>
              <span className="gdp-matchup-team"><TeamLogo teamName={game.home_team} sportKey={game.sport_key} size={28} />{game.home_team}</span>
            </div>
            <div className="gdp-meta">
              {game.is_live ? (
                <span className="live-badge"><span className="live-dot" />LIVE</span>
              ) : (
                <span className="gdp-time">{formatDateTime(game.commence_time)}</span>
              )}
            </div>

            {/* Live Scoreboard */}
            {game.is_live && (game.home_score != null || game.period != null || game.inning_detail || game.is_halftime) && (
              <div className="gdp-scoreboard">
                {game.home_score != null && (() => {
                  const isTennis = game.sport_key?.includes('tennis');
                  const [homeGames, awayGames] = isTennis && game.clock ? game.clock.split('-').map(Number) : [null, null];
                  return isTennis ? (
                    <table className="gdp-tennis-score">
                      <thead>
                        <tr>
                          <th />
                          <th className="gdp-tennis-col">Sets</th>
                          <th className="gdp-tennis-col">Games</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="gdp-score-team">{game.home_team}</td>
                          <td className="gdp-tennis-val"><strong>{game.home_score}</strong></td>
                          <td className="gdp-tennis-val">{homeGames ?? '-'}</td>
                        </tr>
                        <tr>
                          <td className="gdp-score-team">{game.away_team}</td>
                          <td className="gdp-tennis-val"><strong>{game.away_score ?? '-'}</strong></td>
                          <td className="gdp-tennis-val">{awayGames ?? '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <>
                      <div className="gdp-score-row">
                        <span className="gdp-score-team">{game.home_team}</span>
                        <span className="gdp-score">{game.home_score}</span>
                      </div>
                      <div className="gdp-score-row">
                        <span className="gdp-score-team">{game.away_team}</span>
                        <span className="gdp-score">{game.away_score ?? '-'}</span>
                      </div>
                    </>
                  );
                })()}
                {game.is_halftime && (
                  <div className="gdp-period">Halftime</div>
                )}
                {!game.is_halftime && game.period != null && !game.sport_key?.includes('baseball') && (
                  <div className="gdp-period">
                    {formatPeriod(game.period, game.sport_key)}
                    {!game.sport_key?.includes('tennis') && game.clock ? ` · ${game.clock}` : ''}
                  </div>
                )}
                {!game.is_halftime && game.inning_detail && game.sport_key?.includes('baseball') && (
                  <div className="gdp-baseball-situation">
                    <span className="gdp-inning-label">{game.inning_detail}</span>
                    <div className="gdp-base-diamond">
                      <div className={`gdp-base gdp-base-2b${game.on_second ? ' on' : ''}`} />
                      <div className="gdp-base-mid-row">
                        <div className={`gdp-base gdp-base-3b${game.on_third ? ' on' : ''}`} />
                        <div className="gdp-base-gap" />
                        <div className={`gdp-base gdp-base-1b${game.on_first ? ' on' : ''}`} />
                      </div>
                    </div>
                    <div className="gdp-outs-wrap">
                      <span className="gdp-sit-label">OUT</span>
                      <div className="gdp-outs-dots">
                        {[0, 1, 2].map(i => (
                          <div key={i} className={`gdp-out-dot${i < (game.outs ?? 0) ? ' on' : ''}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Opportunities — server-side computed for consistency with EV+/Arb pages */}
          {((game.arbitrage_opportunities?.length > 0) || (game.ev_opportunities?.length > 0)) && (
            <div className="game-modal-opportunities">
              {game.arbitrage_opportunities?.map((arb, idx) => (
                <div key={idx} className="game-modal-opportunity arb-opportunity">
                  <div className="opportunity-badge arb-badge">
                    <AlertCircle size={16} />
                    <span>{arb.profit_percentage}% Arbitrage</span>
                  </div>
                  <div className="opportunity-details">
                    <div className="opp-detail-row">
                      <SportsbookLogo bookKey={arb.home_sportsbook_key} size={16} />
                      <span>{game.home_team} @ {arb.home_sportsbook}: {formatOdds(arb.home_odds)}</span>
                    </div>
                    {arb.is_three_way && (
                      <div className="opp-detail-row">
                        <SportsbookLogo bookKey={arb.draw_sportsbook_key} size={16} />
                        <span>Draw @ {arb.draw_sportsbook}: {formatOdds(arb.draw_odds)}</span>
                      </div>
                    )}
                    <div className="opp-detail-row">
                      <SportsbookLogo bookKey={arb.away_sportsbook_key} size={16} />
                      <span>{game.away_team} @ {arb.away_sportsbook}: {formatOdds(arb.away_odds)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {game.ev_opportunities?.map((ev, idx) => (
                <div key={idx} className="game-modal-opportunity ev-opportunity">
                  <div className="opportunity-badge ev-badge">
                    <TrendingUp size={16} />
                    <span>+{ev.ev_percentage}% EV</span>
                  </div>
                  <div className="opportunity-details">
                    <div className="opp-detail-row">
                      <SportsbookLogo bookKey={ev.sportsbook_key} size={16} />
                      <span>{ev.team} @ {ev.sportsbook}: {formatOdds(ev.odds)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="gdp-legend">
            <Star size={11} className="best-star" />
            <span>= best available price across all books</span>
          </div>

          {/* Moneyline */}
          <CollapsibleSection title="Moneyline" defaultOpen={true} badge={game.bookmakers.length}>
            <OddsTable game={game} market="h2h" addToCart={addToCart} isInCart={isInCart} {...trackingProps} />
          </CollapsibleSection>

          {/* Spread — with alt lines slider */}
          {game.has_spreads && (
            <CollapsibleSection title="Spread" defaultOpen={true} badge={(spreadBooksAtPoint || game.bookmakers).filter(b => b.home_spread_odds).length}>
              <OddsTable game={game} market="spreads" altBooks={spreadBooksAtPoint} altPoint={resolvedSpreadPoint} addToCart={addToCart} isInCart={isInCart} {...trackingProps} />
              {allSpreadPoints.length > 1 && (
                <LineSlider
                  points={allSpreadPoints}
                  selected={resolvedSpreadPoint}
                  onSelect={setSelectedSpreadPoint}
                  isMain={pt => pt === mainSpreadPoint}
                />
              )}
            </CollapsibleSection>
          )}

          {/* Total — with alt lines slider */}
          {game.has_totals && (
            <CollapsibleSection title="Total" defaultOpen={true} badge={(totalBooksAtPoint || game.bookmakers).filter(b => b.over_odds).length}>
              <OddsTable game={game} market="totals" altBooks={totalBooksAtPoint} altPoint={resolvedTotalPoint} addToCart={addToCart} isInCart={isInCart} {...trackingProps} />
              {allTotalPoints.length > 1 && (
                <LineSlider
                  points={allTotalPoints}
                  selected={resolvedTotalPoint}
                  onSelect={setSelectedTotalPoint}
                  isMain={pt => pt === mainTotalPoint}
                />
              )}
            </CollapsibleSection>
          )}

          {/* Player Props Section (NBA, NHL, MLB) */}
          {PROPS_ENABLED_SPORTS.includes(game.sport_key) && (
            <PropsSection
              props={props}
              evProps={selectedBooks && selectedBooks.length > 0
                ? evProps.filter(ev => selectedBooks.includes(ev.sportsbook_key))
                : evProps}
              arbProps={selectedBooks && selectedBooks.length > 0
                ? arbProps.filter(arb =>
                    selectedBooks.includes(arb.over_sportsbook_key) &&
                    selectedBooks.includes(arb.under_sportsbook_key))
                : arbProps}
              bestOdds={propsBestOdds}
              loading={propsLoading}
              error={propsError}
              sportKey={game.sport_key}
              eventId={game.id}
              eventName={`${game.home_team} vs ${game.away_team}`}
              bankroll={bankroll}
              kellyFraction={kellyFraction}
              session={session}
              onTrackProp={handleTrackProp}
              selectedBooks={selectedBooks || []}
              playerInfo={playerInfo}
            />
          )}
        </>
      )}
    </div>
  );
}

export default GameDetailPage;
